import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { COMPANY, NAV_LINKS } from "@/data/content";
import { scrollToSection } from "@/lib/scroll";

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    scrollToSection(href);
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 border-b transition-colors duration-300 ${
        scrolled || open ? "border-white/10 bg-black/85 backdrop-blur-md" : "border-transparent bg-transparent"
      }`}
    >
      <div className="mx-auto flex h-[68px] max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <button
          type="button"
          data-testid="header-logo"
          onClick={() => go("#start")}
          className="flex items-center gap-3"
          aria-label="Uusimaa Svets & VVS AB – till startsidan"
        >
          <img src="/assets/logo-white.webp" alt="Uusimaa Svets & VVS AB logotyp" className="h-9 w-auto" />
        </button>

        <nav className="hidden items-center gap-7 lg:flex" aria-label="Huvudmeny">
          {NAV_LINKS.map((link) => (
            <button
              key={link.href}
              type="button"
              data-testid={link.testid}
              onClick={() => go(link.href)}
              className="font-mono text-[11px] uppercase tracking-[0.18em] text-neutral-400 transition-colors duration-200 hover:text-white"
            >
              {link.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            data-testid="header-call-button"
            href={COMPANY.phoneHref}
            className="hidden items-center gap-2 border border-white/20 px-4 py-2.5 font-mono text-[11px] uppercase tracking-[0.15em] text-white transition-colors duration-200 hover:border-white/60 sm:flex"
          >
            <Phone className="h-3.5 w-3.5" />
            {COMPANY.phone}
          </a>
          <button
            type="button"
            data-testid="header-quote-button"
            onClick={() => go("#kontakt")}
            className="hidden bg-white px-4 py-2.5 font-mono text-[11px] uppercase tracking-[0.15em] text-black transition-colors duration-200 hover:bg-neutral-300 lg:block"
          >
            Skicka förfrågan
          </button>
          <button
            type="button"
            data-testid="mobile-menu-button"
            onClick={() => setOpen((v) => !v)}
            className="flex h-10 w-10 items-center justify-center border border-white/20 text-white lg:hidden"
            aria-label={open ? "Stäng meny" : "Öppna meny"}
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden border-t border-white/10 bg-black/95 backdrop-blur-md lg:hidden"
            aria-label="Mobilmeny"
          >
            <div className="flex flex-col px-4 py-4">
              {NAV_LINKS.map((link) => (
                <button
                  key={link.href}
                  type="button"
                  data-testid={`mobile-${link.testid}`}
                  onClick={() => go(link.href)}
                  className="border-b border-white/5 py-4 text-left font-display text-2xl font-semibold uppercase tracking-tight text-neutral-200 last:border-0"
                >
                  {link.label}
                </button>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
